import { useState, useEffect, useMemo } from 'react';
import { fetchStatuses } from '../api/dispatchService';

interface StatusEntry {
  name: string;
  code: string;
  bg_color?: string | null;
  text_color?: string | null;
}

interface StatusMasterData {
  data?: {
    vehicle?: Record<string, StatusEntry>;
    vehicle_location?: Record<string, StatusEntry>;
    reservation?: Record<string, StatusEntry>;
  };
}

interface VehicleStatusItem {
  id: number;
  label: string;
  code: string;
  bg_color?: string;
  text_color?: string;
  color: string;
}

interface LocationStatusItem {
  id: number | null;
  label: string;
  code: string | null;
  bg_color?: string;
  text_color?: string;
}

interface ReservationStatusItem {
  id: number;
  label: string;
  code: string;
  bg_color: string | null;
  text_color: string | null;
}

interface UseStatusMasterOptions {
  locationNullLabel?: string;
}

/**
 * useStatusMaster
 *
 * ステータスマスターを取得し、vehicleStatusList / locationStatusList / reservationStatusList を返す。
 * DayListReservations と VehicleList で同一だったロジックを一元化。
 */
export function useStatusMaster({ locationNullLabel = '-' }: UseStatusMasterOptions = {}) {
  const [statusMaster, setStatusMaster] = useState<StatusMasterData | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchStatuses();
        if (!cancelled) setStatusMaster(data);
      } catch (error) {
        console.error('Failed to fetch status master:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const vehicleStatusList = useMemo((): VehicleStatusItem[] => {
    if (!statusMaster?.data?.vehicle) return [];
    return Object.entries(statusMaster.data.vehicle).map(([id, status]) => ({
      id: Number(id),
      label: status.name,
      code: status.code,
      bg_color: status.bg_color ?? undefined,
      text_color: status.text_color ?? undefined,
      color: 'primary',
    }));
  }, [statusMaster]);

  const locationStatusList = useMemo((): LocationStatusItem[] => {
    if (!statusMaster?.data?.vehicle_location) {
      return [{ id: null, label: locationNullLabel, code: null }];
    }
    const fromApi = Object.entries(statusMaster.data.vehicle_location).map(([id, status]) => ({
      id: Number(id),
      label: status.name,
      code: status.code,
      bg_color: status.bg_color ?? undefined,
      text_color: status.text_color ?? undefined,
    }));
    return [{ id: null, label: locationNullLabel, code: null }, ...fromApi];
  }, [statusMaster, locationNullLabel]);

  const reservationStatusList = useMemo((): ReservationStatusItem[] => {
    if (!statusMaster?.data?.reservation) return [];
    return Object.entries(statusMaster.data.reservation).map(([id, status]) => ({
      id: Number(id),
      label: status.name,
      code: status.code,
      bg_color: status.bg_color || null,
      text_color: status.text_color || null,
    }));
  }, [statusMaster]);

  return { vehicleStatusList, locationStatusList, reservationStatusList };
}
